import { ArrowLeftOutlined, EditOutlined, TeamOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PROJECT_STATUS,
  PROJECT_STATUS_LABELS,
  PROJECT_TYPE_LABELS,
} from '@/constants/project';
import { ROUTES } from '@/constants/routes';
import { ProjectDepartmentMigrationModal } from '@/pages/admin/projects/ProjectDepartmentMigrationModal';
import { ProjectEditDrawer } from '@/pages/admin/projects/ProjectEditDrawer';
import { ProjectProgressTimelineSection } from '@/pages/admin/projects/ProjectProgressTimelineSection';
import { ProjectReportAdminSection } from '@/pages/admin/projects/ProjectReportAdminSection';
import { ApiError } from '@/services/api/client';
import * as projectReportsApi from '@/services/api/project-reports';
import * as projectsApi from '@/services/api/projects';
import type { ProjectDetail, ProjectMemberRecord, ProjectStatus } from '@/types/project';
import { formatDate, formatDateTime } from '@/utils/format-date';

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [displayProgress, setDisplayProgress] = useState<number | null>(null);
  const [members, setMembers] = useState<ProjectMemberRecord[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [migrationOpen, setMigrationOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveSubmitting, setArchiveSubmitting] = useState(false);
  const [archiveForm] = Form.useForm<{ archiveReason: string }>();

  const loadMembers = useCallback(async (projectId: string) => {
    const result = await projectsApi.fetchProjectMembers(projectId);
    setMembers(result.items);
  }, []);

  const loadProject = useCallback(async () => {
    if (!id) {
      return;
    }
    setLoading(true);
    try {
      const detail = await projectsApi.fetchProject(id);
      setProject(detail);
      let progress = detail.progress ?? 0;
      try {
        const reports = await projectReportsApi.fetchProjectReportsByProject(id, {
          page: 1,
          pageSize: 1,
          reportPeriod: 'DAILY',
        });
        if (reports.items[0]) {
          progress = reports.items[0].progressPercent;
        }
      } catch {
        // 无负责人进展时回退任务进度
      }
      setDisplayProgress(progress);
      await loadMembers(id);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载项目详情失败';
      message.error(text);
      setProject(null);
      setDisplayProgress(null);
    } finally {
      setLoading(false);
    }
  }, [id, loadMembers, message]);

  useEffect(() => {
    void loadProject();
  }, [loadProject]);

  const memberCount = project?.memberCount ?? project?._count?.members ?? members.length;

  const memberColumns: ColumnsType<ProjectMemberRecord> = [
    { title: '姓名', dataIndex: ['user', 'name'] },
    { title: '手机号', dataIndex: ['user', 'phone'] },
    {
      title: '角色',
      dataIndex: ['user', 'role'],
      width: 120,
      render: (role: string, record) =>
        record.userId === project?.projectManagerId ? (
          <Space size={4}>
            <Tag color="blue">负责人</Tag>
            <span>{role}</span>
          </Space>
        ) : (
          role
        ),
    },
  ];

  const handleUnarchive = () => {
    if (!id) {
      return;
    }
    modal.confirm({
      title: '确认恢复项目？',
      content: '恢复后项目状态将变为进行中，员工与领导可见（若为成员）。',
      onOk: async () => {
        await projectsApi.unarchiveProject(id);
        message.success('项目已恢复');
        await loadProject();
      },
    });
  };

  const handleArchive = async () => {
    if (!id) {
      return;
    }
    const values = await archiveForm.validateFields();
    setArchiveSubmitting(true);
    try {
      await projectsApi.archiveProject(id, { archiveReason: values.archiveReason.trim() });
      message.success('项目已归档');
      setArchiveOpen(false);
      archiveForm.resetFields();
      await loadProject();
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '归档失败';
      message.error(text);
    } finally {
      setArchiveSubmitting(false);
    }
  };

  if (!id) {
    return null;
  }

  const status = project?.status as ProjectStatus | undefined;
  const autoMembers = project?.memberSource === 'AUTO_SYNC_FROM_DEPARTMENTS';

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(ROUTES.admin.projects)}>
          返回列表
        </Button>
        {project && status === PROJECT_STATUS.ACTIVE && (
          <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
            编辑项目
          </Button>
        )}
        {status === PROJECT_STATUS.ACTIVE && (
          <Button danger onClick={() => setArchiveOpen(true)}>
            归档项目
          </Button>
        )}
        {status === PROJECT_STATUS.ARCHIVED && (
          <Button type="primary" onClick={handleUnarchive}>
            恢复项目
          </Button>
        )}
      </Space>

      <Typography.Title level={4} style={{ marginTop: 0 }}>
        {project?.name ?? '项目详情'}
      </Typography.Title>
      {project?.projectNo && (
        <Typography.Text type="secondary">编号：{project.projectNo}</Typography.Text>
      )}

      {project && project.departmentsConfigured === false ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          message="该项目尚未配置项目部门"
          description="历史项目需先确认部门范围，系统将据此自动同步成员。"
          action={
            <Button size="small" onClick={() => setMigrationOpen(true)}>
              配置部门
            </Button>
          }
        />
      ) : null}

      <Row gutter={[16, 16]} style={{ marginTop: 16, marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="自动计算成员数" value={memberCount} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="项目部门数"
              value={project?.departments?.length ?? project?.departmentCount ?? 0}
            />
          </Card>
        </Col>
      </Row>

      <Card title="基本信息" loading={loading} style={{ marginBottom: 16 }}>
        {project && (
          <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
            <Descriptions.Item label="项目编号">{project.projectNo}</Descriptions.Item>
            <Descriptions.Item label="项目状态">
              <Tag color={project.status === PROJECT_STATUS.ACTIVE ? 'processing' : 'default'}>
                {PROJECT_STATUS_LABELS[project.status]}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="项目类型">
              {PROJECT_TYPE_LABELS[project.projectType]}
            </Descriptions.Item>
            <Descriptions.Item label="项目进度">
              {displayProgress ?? project.progress}%
            </Descriptions.Item>
            <Descriptions.Item label="项目负责人">
              {project.projectManager?.name ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="成员来源">
              {autoMembers ? '按项目部门自动计算' : '历史手工成员（待迁移）'}
            </Descriptions.Item>
            <Descriptions.Item label="项目部门" span={2}>
              {project.departments && project.departments.length > 0 ? (
                <Space size={[4, 4]} wrap>
                  {project.departments.map((department) => (
                    <Tag key={department.id}>
                      {department.name}（{department.code}）
                    </Tag>
                  ))}
                </Space>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="创建人">{project.createdBy?.name ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="开始时间">{formatDate(project.startDate)}</Descriptions.Item>
            <Descriptions.Item label="结束时间">{formatDate(project.endDate)}</Descriptions.Item>
            <Descriptions.Item label="归档时间">{formatDateTime(project.archivedAt)}</Descriptions.Item>
            <Descriptions.Item label="归档原因" span={2}>
              {project.archiveReason?.trim() || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="项目简介" span={2}>
              {project.summary?.trim() || '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      <Card
        title="负责人进展"
        extra={
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            只读 · 负责人在写日报时填报
          </Typography.Text>
        }
        loading={loading}
        style={{ marginBottom: 16 }}
      >
        {project ? <ProjectProgressTimelineSection projectId={project.id} /> : null}
      </Card>

      <Card
        title="项目日报"
        extra={
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            只读 · 来自成员写日报时的项目关联
          </Typography.Text>
        }
        loading={loading}
        style={{ marginBottom: 16 }}
      >
        {project ? <ProjectReportAdminSection project={project} /> : null}
      </Card>

      <Card
        title="项目成员"
        extra={
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            只读 · 由项目部门自动同步
          </Typography.Text>
        }
        loading={loading}
      >
        <Table
          rowKey="id"
          columns={memberColumns}
          dataSource={members}
          pagination={false}
          locale={{ emptyText: '暂无成员' }}
        />
      </Card>

      <ProjectEditDrawer
        open={editOpen}
        project={project}
        onClose={() => setEditOpen(false)}
        onSaved={() => void loadProject()}
      />

      <ProjectDepartmentMigrationModal
        open={migrationOpen}
        project={project}
        onClose={() => setMigrationOpen(false)}
        onConfirmed={() => void loadProject()}
      />

      <Modal
        title="归档项目"
        open={archiveOpen}
        confirmLoading={archiveSubmitting}
        onCancel={() => {
          setArchiveOpen(false);
          archiveForm.resetFields();
        }}
        onOk={() => void handleArchive()}
        okText="确认归档"
        destroyOnClose
      >
        <Form form={archiveForm} layout="vertical">
          <Form.Item
            name="archiveReason"
            label="归档原因"
            rules={[{ required: true, message: '请填写归档原因' }, { max: 255 }]}
          >
            <Input.TextArea rows={3} placeholder="请输入归档原因" showCount maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
